import { Input } from '@/components';
import {
  FilterSelect,
  QueryStates,
  RunnerPage,
  uniqueOptions,
} from '@/features/runner-monitor/RunnerPage';
import { RunnersTable } from '@/features/runner-monitor/RunnersTable';
import { useRunnerMachines, useRunners } from '@/features/runner-monitor/hooks';
import { A, useSearchParams } from '@solidjs/router';
import type { Runner } from '@uchat-team-management/shared';
import { For } from 'solid-js';

type Params = { machine?: string; os?: string; repo?: string; status?: string; q?: string };

export default function RunnersList() {
  const runners = useRunners();
  const machines = useRunnerMachines();
  const [params, setParams] = useSearchParams<Params>();

  const machineOs = (id: string | null) => machines.data?.data.find((m) => m.id === id)?.os;
  const visible = () =>
    (runners.data?.data ?? []).filter(
      (r) =>
        (!params.machine || r.machineId === params.machine) &&
        (!params.os || machineOs(r.machineId) === params.os) &&
        (!params.repo || r.repo === params.repo) &&
        (!params.status || r.status === params.status) &&
        (!params.q || r.name.toLowerCase().includes(params.q.toLowerCase())),
    );
  /** Runners grouped under the machine they are installed on. */
  const groups = () => {
    const byMachine = new Map<string, { id: string | null; name: string; runners: Runner[] }>();
    for (const r of visible()) {
      const key = r.machineId ?? '';
      const g = byMachine.get(key) ?? {
        id: r.machineId,
        name: r.machineName ?? 'Not linked to a machine (no agent reported it)',
        runners: [],
      };
      g.runners.push(r);
      byMachine.set(key, g);
    }
    return [...byMachine.values()].sort(
      (a, b) => (a.id ? 0 : 1) - (b.id ? 0 : 1) || a.name.localeCompare(b.name),
    );
  };

  return (
    <RunnerPage
      title="Runners"
      sub="Grouped by the machine they run on"
      updatedAt={runners.dataUpdatedAt}
    >
      <div class="flex flex-wrap items-end gap-3">
        <FilterSelect
          label="Machine"
          value={params.machine}
          options={(machines.data?.data ?? []).map((m) => ({ value: m.id, label: m.name }))}
          onChange={(machine) => setParams({ machine })}
        />
        <FilterSelect
          label="OS"
          value={params.os}
          options={[
            { value: 'macos', label: 'macOS' },
            { value: 'windows', label: 'Windows' },
          ]}
          onChange={(os) => setParams({ os })}
        />
        <FilterSelect
          label="Repo"
          value={params.repo}
          options={uniqueOptions((runners.data?.data ?? []).map((r) => r.repo))}
          onChange={(repo) => setParams({ repo })}
        />
        <FilterSelect
          label="Status"
          value={params.status}
          options={[
            { value: 'busy', label: 'Busy' },
            { value: 'idle', label: 'Idle' },
            { value: 'offline', label: 'Offline' },
          ]}
          onChange={(status) => setParams({ status })}
        />
        <Input
          type="search"
          label="Runner"
          placeholder="Name contains…"
          value={params.q ?? ''}
          onInput={(e) => setParams({ q: e.currentTarget.value || undefined })}
        />
      </div>
      <QueryStates
        pending={runners.isPending}
        error={runners.error}
        empty={visible().length === 0}
        emptyTitle={(runners.data?.data.length ?? 0) > 0 ? 'No runners match' : 'No runners yet'}
        emptyBody="Runners appear once an agent reports them or GitHub sync lists them."
      >
        <For each={groups()}>
          {(g) => (
            <div class="space-y-2">
              <h2 class="text-sm font-semibold">
                {g.id ? (
                  <A href={`/runners/machines/${g.id}`} class="hover:underline">
                    {g.name}
                  </A>
                ) : (
                  <span class="text-neutral-500">{g.name}</span>
                )}
              </h2>
              <RunnersTable runners={g.runners} />
            </div>
          )}
        </For>
      </QueryStates>
    </RunnerPage>
  );
}
